import React from 'react';
import VisaPageLayout, { FaqItem, ProcessStep, RequirementItem, VisaPlan } from './visa-page-layout';
import { useVisaPacksByCountry } from '../hooks/useVisaPacksByCountry';


const plans: VisaPlan[] = [
  { id: 'bh-14', name: '14-Day eVisa', entryType: 'Single Entry', validity: 'Use within 30 days of issue', stay: 'Up to 14 days in Bahrain', processing: '2–3 business days', price: '30', currency: 'BHD' },
  { id: 'bh-30', name: '1-Month eVisa', entryType: 'Single Entry', validity: 'Use within 30 days of issue', stay: 'Up to 30 days in Bahrain', processing: '2–3 business days', price: '40', currency: 'BHD', featured: true },
  { id: 'bh-90', name: '3-Month eVisa', entryType: 'Multiple Entry', validity: 'Valid for 3 months from issue', stay: 'Up to 30 days per visit', processing: '3–5 business days', price: '75', currency: 'BHD' },
];

const requirements: RequirementItem[] = [
  { label: 'Passport copy', detail: 'Valid for at least 6 months from your arrival date.' },
  { label: 'Passport-size photo', detail: 'Recent, plain background, matching standard visa photo specifications.' },
  { label: 'Confirmed return ticket', detail: 'Round-trip or onward booking showing your departure from Bahrain.' },
  { label: 'Hotel booking', detail: 'Confirmed accommodation for the length of your stay.' },
  { label: 'Bank statement (if requested)', detail: 'Occasionally requested for certain nationalities as proof of funds.' },
];

const goodToKnow: string[] = [
  'Many nationalities are eligible for visa-on-arrival in Bahrain — we\'ll confirm if pre-arranging an eVisa is actually necessary for you.',
  'The King Fahd Causeway link to Saudi Arabia makes Bahrain an easy add-on to a wider Gulf itinerary.',
  'The 3-month multiple-entry option suits travelers doing repeat business or family visits.',
  'Ramadan hours can affect processing — we\'ll flag this if your trip falls during that period.',
];

const steps: ProcessStep[] = [
  { title: 'Upload your documents', text: 'Send your passport scan, photo and travel dates through our secure form.' },
  { title: 'We review & submit', text: 'Our team checks your file against current Bahrain eVisa requirements before filing.' },
  { title: 'Track your status', text: 'We\'ll update you by WhatsApp or email while your application is processed.' },
  { title: 'Receive your eVisa', text: 'Download your approved eVisa and keep a printed copy with your passport.' },
];

const faqs: FaqItem[] = [
  { question: 'Do I really need to apply in advance, or can I get a visa on arrival?', answer: 'It depends on your nationality — some travelers are eligible for visa-on-arrival, which can be faster than pre-applying. Tell us your passport country and we\'ll advise the quickest route.' },
  { question: 'Can I visit Saudi Arabia from Bahrain on the same trip?', answer: 'Yes, many travelers combine both. You\'ll need a separate Saudi visa — we can arrange that alongside your Bahrain application.' },
  { question: 'How far in advance should I apply?', answer: 'We recommend at least 5–7 days before travel to allow for standard processing and any follow-up document requests.' },
  { question: 'Is the eVisa accepted at all entry points?', answer: 'It\'s accepted at Bahrain International Airport and the King Fahd Causeway. We\'ll confirm entry point requirements for your specific itinerary.' },
];

export default function VisaBahrainPage() {
  const { plans, loading, error } = useVisaPacksByCountry('BHR');
  return (
    <VisaPageLayout
      countryName="Bahrain"
      flagEmoji="🇧🇭"
      heroEyebrow="Kingdom of Bahrain"
      heroImage="https://images.unsplash.com/photo-1748373448914-1d7f882700e2?w=1600&q=80&fit=crop&crop=center"
      heroTitleLead="Your"
      heroTitleHighlight="Bahrain"
      heroTitleTail="visa, without the guesswork"
      heroSubtitle="A short flight, a different pace — we prepare your Bahrain tourist eVisa so your island getaway starts the moment you land."
      trustStats={[
        { value: '2–5', label: 'Business Days' },
        { value: '97%', label: 'Approval Rate' },
        { value: '1,800+', label: 'Bahrain Visas Issued' },
      ]}
      plans={plans}
       plansLoading={loading}
      plansError={error}
      requirements={requirements}
      goodToKnow={goodToKnow}
      steps={steps}
      faqs={faqs}
      ctaSubtitle="Send your documents today — most complete files are filed with Bahrain's eVisa system within one business day."
    />
  );
}